import { useState } from 'react';
import { useAuthStore } from '@/state/authStore';
import { useGameStore } from '@/state/gameStore';
import { deleteCharacter, flushNow, stopSync } from '@/state/profileSync';
import { deleteLocalCharacter as deleteLocalCharFromList, listLocalCharacters } from '@/state/localCharacters';
import { useT, useLangStore, LANG_OPTIONS } from '@/i18n';
import { itemsInSet } from '@/domain/items';
import { randomId } from '@/domain/player';
import type { EquipmentInstance } from '@/domain/types';
import { Icon } from './Icon';
import './SettingsModal.css';

/** sessionStorage key that signals App.tsx (post-auth) to take the local
 *  character list and append each onto the freshly-signed-in account. */
export const PENDING_CONVERT_KEY = 'cve.pendingConvert';

/** sessionStorage key that signals App.tsx, on the next player-null
 *  transition, to route to the character-select screen instead of the
 *  default auth-screen fallback. Used by "Charakter wechseln". */
export const PENDING_SWITCH_KEY = 'cve.pendingSwitch';

interface SettingsModalProps {
  onClose: () => void;
}

export function SettingsModal({ onClose }: SettingsModalProps) {
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const resetPlayer = useGameStore((s) => s.resetPlayer);
  const player = useGameStore((s) => s.player);
  const t = useT();
  // Subscribe to lang so the picker's active-state re-renders when the
  // user clicks a different language. Calling .getState() inside render
  // would leave the highlighted button stale until the next external
  // re-render trigger.
  const currentLang = useLangStore((s) => s.lang);
  const setLang = useLangStore((s) => s.setLang);

  // Local-mode detection: a player created via "Ohne Konto spielen" carries
  // a synthetic id starting with "local-". Used to swap the Settings UI
  // between the authenticated and anonymous variants below.
  const isLocalMode = !user && (player?.id?.startsWith('local-') ?? false);

  const [busy, setBusy] = useState(false);

  const handleLogout = async () => {
    if (!confirm('Wirklich abmelden? Dein Spielstand bleibt im Account erhalten.')) return;
    setBusy(true);
    await flushNow();
    stopSync();
    useGameStore.setState({ player: null });
    await signOut();
    onClose();
  };

  /** Local-only: return to the auth screen WITHOUT deleting the local
   *  character. The next "Ohne Konto spielen" click auto-resumes. */
  const handleReturnToLogin = () => {
    if (!confirm('Zum Login-Bildschirm zurück? Dein lokaler Spielstand bleibt erhalten und kann später wieder geladen werden.')) return;
    useGameStore.setState({ player: null });
    onClose();
  };

  /** Local-only: switch to a different local character via the select
   *  screen. Same mechanism as the account "Charakter wechseln". */
  const handleSwitchLocalCharacter = () => {
    try { sessionStorage.setItem(PENDING_SWITCH_KEY, '1'); } catch { /* ignore */ }
    useGameStore.setState({ player: null });
    onClose();
  };

  /** Local-only: delete just the active local character from the multi-
   *  char list. If other characters remain, route to select; otherwise
   *  fall back to auth. */
  const handleDeleteLocalCharacter = () => {
    if (!confirm('Diesen lokalen Charakter UNWIDERRUFLICH löschen? Andere lokale Charaktere bleiben erhalten.')) return;
    if (!confirm('Wirklich? Es gibt kein Backup, dieser Charakter ist danach komplett weg.')) return;
    if (player) deleteLocalCharFromList(player.id);
    try { sessionStorage.setItem(PENDING_SWITCH_KEY, '1'); } catch { /* ignore */ }
    // resetPlayer also clears the Zustand persist cache.
    resetPlayer();
    useGameStore.setState({ player: null });
    onClose();
  };

  /** Switch character — flush the current state, clear player. App.tsx
   *  picks up the PENDING_SWITCH_KEY flag and routes to selectCharacter. */
  const handleSwitchCharacter = async () => {
    setBusy(true);
    await flushNow();
    stopSync();
    try { sessionStorage.setItem(PENDING_SWITCH_KEY, '1'); } catch { /* ignore — fall back to auth */ }
    useGameStore.setState({ player: null });
    onClose();
  };

  /** Delete just the active character (not the whole account). Cascades
   *  equipment / inventory / miners / boosts via FK. The remaining
   *  characters stay; user lands back on the character-select screen. */
  const handleDeleteRunner = async () => {
    if (!confirm(t.character.deleteConfirm)) return;
    if (!confirm('SICHERHEITSFRAGE: Inventar, Equipment, Miner und Level dieses Charakters werden GELÖSCHT (andere Charaktere bleiben). Fortfahren?')) return;
    if (!user || !player) return;
    setBusy(true);
    try {
      // Stop sync so the about-to-be-deleted character can't be re-upserted
      // mid-delete.
      stopSync();
      await deleteCharacter(player.id);
      // Drop the in-memory copy; route back to select-screen via the
      // PENDING_SWITCH_KEY signal (App.tsx exit-watcher).
      try { sessionStorage.setItem(PENDING_SWITCH_KEY, '1'); } catch { /* ignore */ }
      resetPlayer();
      useGameStore.setState({ player: null });
      onClose();
    } catch (e) {
      alert(`Löschen fehlgeschlagen: ${(e as Error).message}`);
      setBusy(false);
    }
  };

  return (
    <div className="settings-modal__overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-modal__header">
          <h2 className="settings-modal__title">
            <Icon name="settings" size={20} style={{ verticalAlign: '-4px', marginRight: '0.4rem' }} />
            {t.settings.title}
          </h2>
          <button className="settings-modal__close" onClick={onClose} aria-label="Schließen">✕</button>
        </div>

        <section className="settings-modal__section">
          <h3 className="settings-modal__section-title">{t.settings.language}</h3>
          <div className="settings-modal__lang-row">
            {LANG_OPTIONS.map((opt) => {
              const active = currentLang === opt.code;
              return (
                <button
                  key={opt.code}
                  type="button"
                  className={`btn settings-modal__lang-btn ${active ? 'settings-modal__lang-btn--active' : ''}`}
                  onClick={() => setLang(opt.code)}
                  aria-pressed={active}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </section>

        <section className="settings-modal__section">
          <h3 className="settings-modal__section-title">
            {isLocalMode ? 'Lokaler Modus' : 'Account'}
          </h3>
          {isLocalMode ? (
            <div className="settings-modal__row">
              <span className="settings-modal__label">Modus</span>
              <span className="settings-modal__value" style={{ color: 'var(--color-cyan)' }}>
                🎮 Ohne Konto · nur dieses Gerät
              </span>
            </div>
          ) : (
            <>
              <div className="settings-modal__row">
                <span className="settings-modal__label">E-Mail</span>
                <span className="settings-modal__value">{user?.email ?? '—'}</span>
              </div>
              <div className="settings-modal__row">
                <span className="settings-modal__label">User-ID</span>
                <span className="settings-modal__value settings-modal__value--mono">
                  {(user?.id ?? '').slice(0, 8)}…
                </span>
              </div>
            </>
          )}
        </section>

        <section className="settings-modal__section">
          <h3 className="settings-modal__section-title">{t.settings.actions}</h3>
          {isLocalMode ? (
            <>
              <button
                className="btn btn--primary settings-modal__action"
                onClick={() => {
                  const locals = listLocalCharacters();
                  const n = locals.length;
                  if (n === 0) {
                    alert('Keine lokalen Charaktere zum Übertragen vorhanden.');
                    return;
                  }
                  if (!confirm(
                    `${n} lokale${n > 1 ? 'r' : 'r'} Charakter${n > 1 ? 'e' : ''} wird in einen ` +
                    `(neuen oder bestehenden) Account übertragen.\n\n` +
                    `1. Du wirst zum Login geleitet.\n` +
                    `2. Nach erfolgreichem Login werden die lokalen Charaktere in freien ` +
                    `Account-Slots angelegt (max. 3 pro Account).\n` +
                    `3. Sind alle Slots voll, bleiben Reste lokal erhalten.\n\nFortfahren?`,
                  )) return;
                  // Mark conversion intent — App.tsx picks this up in
                  // hydrateForUser right after a successful sign-in/sign-up
                  // and runs the append-into-free-slots routine.
                  try {
                    sessionStorage.setItem(PENDING_CONVERT_KEY, '1');
                  } catch {
                    // Private-mode browsers may reject sessionStorage; fall
                    // back to a hard alert so the user knows.
                    alert('Konnte Konvertierungs-Marker nicht setzen (Privater Modus?). Bitte normalen Browser-Modus verwenden.');
                    return;
                  }
                  useGameStore.setState({ player: null });
                  onClose();
                  // App.tsx exit-watcher routes to auth screen automatically.
                }}
                disabled={busy}
              >
                ✦ Konto erstellen + Spielstand übertragen
              </button>
              <button
                className="btn settings-modal__action"
                onClick={handleSwitchLocalCharacter}
                disabled={busy}
              >
                ↻ Charakter wechseln
              </button>
              <button
                className="btn btn--ghost settings-modal__action"
                onClick={handleReturnToLogin}
                disabled={busy}
              >
                <Icon name="logout" size={16} style={{ marginRight: '0.4rem', verticalAlign: '-3px' }} />
                Zum Login zurück
              </button>
              <button
                className="btn btn--danger settings-modal__action"
                onClick={handleDeleteLocalCharacter}
                disabled={busy}
              >
                <Icon name="trash" size={16} style={{ marginRight: '0.4rem', verticalAlign: '-3px' }} />
                Diesen Charakter löschen
              </button>
            </>
          ) : (
            <>
              <button
                className="btn settings-modal__action"
                onClick={() => { void handleSwitchCharacter(); }}
                disabled={busy}
              >
                ↻ Charakter wechseln
              </button>
              <button
                className="btn btn--ghost settings-modal__action"
                onClick={handleLogout}
                disabled={busy}
              >
                <Icon name="logout" size={16} style={{ marginRight: '0.4rem', verticalAlign: '-3px' }} />
                Abmelden
              </button>
              <button
                className="btn btn--danger settings-modal__action"
                onClick={handleDeleteRunner}
                disabled={busy}
              >
                <Icon name="trash" size={16} style={{ marginRight: '0.4rem', verticalAlign: '-3px' }} />
                Charakter löschen
              </button>
            </>
          )}
        </section>

        <section className="settings-modal__section">
          <h3 className="settings-modal__section-title">Dev / QA</h3>
          <button
            className="btn btn--ghost settings-modal__action"
            onClick={() => {
              // Spawn the complete Hollow-Knight set into stash for QA/visual
              // verification without farming. Each piece rolled at base stats.
              const pieces = itemsInSet('hollow_knight');
              const instances: EquipmentInstance[] = pieces.map((def) => ({
                id: randomId('eq'),
                itemId: def.id,
                upgradeLevel: 0,
              }));
              useGameStore.setState((state) => {
                if (!state.player) return state;
                return {
                  player: {
                    ...state.player,
                    equipmentStash: [...state.player.equipmentStash, ...instances],
                  },
                };
              });
              alert(`HK-Set (${instances.length} Items) ins Stash gelegt.`);
            }}
            disabled={busy}
          >
            + Hollow-Knight Set ins Stash
          </button>
          <button
            className="btn btn--ghost settings-modal__action"
            onClick={() => {
              const pieces = itemsInSet('vault_knight');
              const instances: EquipmentInstance[] = pieces.map((def) => ({
                id: randomId('eq'),
                itemId: def.id,
                upgradeLevel: 0,
              }));
              useGameStore.setState((state) => {
                if (!state.player) return state;
                return {
                  player: {
                    ...state.player,
                    equipmentStash: [...state.player.equipmentStash, ...instances],
                  },
                };
              });
              alert(`VK-Set (${instances.length} Items) ins Stash gelegt.`);
            }}
            disabled={busy}
          >
            + Vault-Knight Set ins Stash
          </button>
        </section>

        <section className="settings-modal__footer">
          <span>Cyber-Vault Extraction · Build {(import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev'}</span>
        </section>
      </div>
    </div>
  );
}
